"use client";

import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import clsx from "clsx";

type CustomDrawerProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  side?: "left" | "right";
  widthClass?: string; // e.g. "w-full max-w-xl"
  maxWidth?: string; // optional additional max-w-* class
  direction?: "ltr" | "rtl";
  /** When true, disables page scrolling while the drawer is open (default). */
  lockScroll?: boolean;
  closeOnOutsideClick?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  ariaLabel?: string;
  ariaLabelledby?: string;
};

function usePortalRoot(id = "drawer-root") {
  const [el, setEl] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    let root = document.getElementById(id);
    if (!root) {
      root = document.createElement("div");
      root.id = id;
      document.body.appendChild(root);
    }
    setEl(root);
  }, [id]);
  return el;
}

const BODY_LOCK_COUNT_ATTR = "data-custom-drawer-lock-count";
const BODY_PREV_OVERFLOW_ATTR = "data-custom-drawer-prev-body-overflow";
const HTML_PREV_OVERFLOW_ATTR = "data-custom-drawer-prev-html-overflow";

function acquireBodyLock() {
  const body = document.body;
  const html = document.documentElement;
  const currentCount = Number(body.getAttribute(BODY_LOCK_COUNT_ATTR) ?? "0");

  if (currentCount === 0) {
    body.setAttribute(BODY_PREV_OVERFLOW_ATTR, body.style.overflow);
    html.setAttribute(HTML_PREV_OVERFLOW_ATTR, html.style.overflow);
  }

  body.setAttribute(BODY_LOCK_COUNT_ATTR, String(currentCount + 1));
  body.style.overflow = "hidden";
  html.style.overflow = "hidden";
  html.classList.add("overflow-hidden");
}

function releaseBodyLock() {
  const body = document.body;
  const html = document.documentElement;
  const currentCount = Number(body.getAttribute(BODY_LOCK_COUNT_ATTR) ?? "0");

  if (currentCount <= 1) {
    const previousBodyOverflow =
      body.getAttribute(BODY_PREV_OVERFLOW_ATTR) ?? "";
    const previousHtmlOverflow =
      html.getAttribute(HTML_PREV_OVERFLOW_ATTR) ?? "";

    body.style.overflow = previousBodyOverflow;
    html.style.overflow = previousHtmlOverflow;
    html.classList.remove("overflow-hidden");

    body.removeAttribute(BODY_LOCK_COUNT_ATTR);
    body.removeAttribute(BODY_PREV_OVERFLOW_ATTR);
    html.removeAttribute(HTML_PREV_OVERFLOW_ATTR);
    return;
  }

  body.setAttribute(BODY_LOCK_COUNT_ATTR, String(currentCount - 1));
}

function useBodyLock(locked: boolean) {
  const appliedRef = React.useRef(false);

  React.useEffect(() => {
    if (locked && !appliedRef.current) {
      acquireBodyLock();
      appliedRef.current = true;
    } else if (!locked && appliedRef.current) {
      releaseBodyLock();
      appliedRef.current = false;
    }

    return () => {
      if (appliedRef.current) {
        releaseBodyLock();
        appliedRef.current = false;
      }
    };
  }, [locked]);
}

export function CustomDrawer({
  open,
  onOpenChange,
  side = "left",
  widthClass = "w-full max-w-[640px]",
  maxWidth,
  direction = "ltr",
  lockScroll = true,
  closeOnOutsideClick = true,
  header,
  footer,
  children,
  className,
  bodyClassName,
  ariaLabel,
  ariaLabelledby,
}: CustomDrawerProps) {
  const portal = usePortalRoot();
  useBodyLock(lockScroll && open);
  const reduceMotion = useReducedMotion();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const frame = window.requestAnimationFrame(() => panelRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  React.useEffect(() => {
    if (open) return;
    previousFocusRef.current?.focus?.();
    previousFocusRef.current = null;
  }, [open]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onOpenChange(false);
      return;
    }
    if (event.key !== "Tab" || !panelRef.current) return;
    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const mergedWidth = clsx(widthClass, maxWidth);
  const from = side === "left" ? "-100%" : "100%";

  const content = (
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            key="drawer-overlay"
            type="button"
            aria-label="Close"
            className="fixed inset-0 z-[70] h-full w-full bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2, ease: "easeOut" }}
            onClick={() => {
              if (closeOnOutsideClick) onOpenChange(false);
            }}
          />

          <motion.div
            key="drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            tabIndex={-1}
            ref={panelRef}
            onKeyDown={handleKeyDown}
            dir={direction}
            className={clsx(
              "fixed top-0 bottom-0 z-[80] flex flex-col bg-white shadow-2xl outline-none",
              side === "left" ? "left-0" : "right-0",
              mergedWidth,
              className,
            )}
            initial={{ x: from, opacity: 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: from, opacity: 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.24,
              ease: [0.22, 0.61, 0.36, 1],
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {header ? (
              <div className="shrink-0 border-b border-neutral-200">
                {header}
              </div>
            ) : null}
            <div
              className={clsx(
                "flex-1 overflow-y-auto overscroll-contain",
                bodyClassName,
              )}
            >
              {children}
            </div>
            {footer ? (
              <div className="shrink-0 border-t border-neutral-200">
                {footer}
              </div>
            ) : null}
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );

  if (!portal) return null;
  return createPortal(content, portal);
}
